// AI Chat API client — the knowledge admin CRUD side.
//
// The chat itself talks over a WebSocket (opened directly in AiChat.jsx,
// not through this file — there's no apiFetch-style wrapper for WS
// connections since the auth token travels as a `?token=` query param on
// the connection URL, not a header). This file only wraps the small
// admin HTTP API (GET/PATCH/DELETE /api/knowledge) — mirrors
// `tools/store-cro-audit/storeCROAuditApi.js`'s apiFetch shape.

import { apiFetch } from '../../lib/apiFetch.js';
import { API_HOSTS, AI_CHAT_WS_URL } from '../../config.js';

const ADMIN_API_BASE_URL = API_HOSTS.aiChatAdmin;

export { AI_CHAT_WS_URL };

async function readErrorMessage(response, fallback) {
  try {
    const cloned = response.clone();
    const data = await cloned.json();
    if (data && data.error) return data.error;
  } catch { /* fall through to text */ }
  try {
    const text = await response.text();
    if (text) return text.slice(0, 300);
  } catch { /* ignore */ }
  return fallback || `HTTP ${response.status}`;
}

export const aiChatApi = {
  // ── Knowledge — single-item read (any authenticated user; used by the
  //    in-chat citation chip to show what a lesson actually says) ───────
  async getKnowledge(knowledgeId) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge/${knowledgeId}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load learned fact: ${response.status}`));
    }
    return response.json();
  },

  // ── Knowledge — admin list/edit/delete ──────────────────────────────
  async listKnowledge(status) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge${qs}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load knowledge: ${response.status}`));
    }
    return response.json();
  },

  async createKnowledge({ content, category, applies_to }) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, category, applies_to }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to add: ${response.status}`));
    }
    return response.json();
  },

  async updateKnowledge(knowledgeId, updates) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge/${knowledgeId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to update: ${response.status}`));
    }
    return response.json();
  },

  async deleteKnowledge(knowledgeId) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge/${knowledgeId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to delete: ${response.status}`));
    }
    return response.json();
  },

  // Promote/reject a pending row from the admin table. Closes the gap
  // where a manually-submitted (non-admin) "pending" entry had no path to
  // "approved" outside of the in-chat bubble, which only exists for
  // agent-proposed lessons. Admin-only server-side (see
  // admin-api/routes/knowledge.py).
  async approveKnowledge(knowledgeId) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge/${knowledgeId}/approve`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to approve: ${response.status}`));
    }
    return response.json();
  },

  async rejectKnowledge(knowledgeId) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/knowledge/${knowledgeId}/reject`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to reject: ${response.status}`));
    }
    return response.json();
  },

  // ── Schema metadata — admin list/create/edit-column/delete/sync ──────
  // Added 3 Aug 2026 for the retrieval-architecture rebuild (see
  // ai-chat-tracker.md) — table/column business context the agent's
  // get_table_schema tool searches, instead of a static glossary file.
  async listSchema() {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/schema`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load schema: ${response.status}`));
    }
    return response.json();
  },

  async createSchema({ table_name, business_summary, columns }) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/schema`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ table_name, business_summary, columns }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to add table: ${response.status}`));
    }
    return response.json();
  },

  async updateColumnMeaning(tableName, columnName, businessMeaning) {
    const response = await apiFetch(
      `${ADMIN_API_BASE_URL}/api/schema/${encodeURIComponent(tableName)}/columns/${encodeURIComponent(columnName)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_meaning: businessMeaning }),
      }
    );
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to update column: ${response.status}`));
    }
    return response.json();
  },

  async deleteSchema(tableName) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/schema/${encodeURIComponent(tableName)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to delete table: ${response.status}`));
    }
    return response.json();
  },

  async syncSchema(schemas) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/schema/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(schemas ? { schemas } : {}),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Sync failed: ${response.status}`));
    }
    return response.json();
  },

  // ── Sample queries — admin list/create/edit/delete ───────────────────
  async listQueries() {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/queries`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load queries: ${response.status}`));
    }
    return response.json();
  },

  async createQuery({ title, description, sql, tags }) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/queries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, description, sql, tags }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to add query: ${response.status}`));
    }
    return response.json();
  },

  async updateQuery(queryId, updates) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/queries/${queryId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to update query: ${response.status}`));
    }
    return response.json();
  },

  async deleteQuery(queryId) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/queries/${queryId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to delete query: ${response.status}`));
    }
    return response.json();
  },

  // ── Audit report — read-only endpoints (18 Aug 2026) ─────────────────
  // Agent Audit & Evaluation Framework — reads pre-computed daily
  // summaries from S3 via the admin API, and DynamoDB for drill-down.
  async getAuditSummary(date) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/audit/summary/${date}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load audit summary: ${response.status}`));
    }
    return response.json();
  },

  async listAuditDates(days = 14) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/audit/summaries?days=${days}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to list audit dates: ${response.status}`));
    }
    return response.json();
  },

  async getAuditDetails(date) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/audit/details/${date}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load audit details: ${response.status}`));
    }
    return response.json();
  },

  async getAuditTrend(days = 14) {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/audit/trend?days=${days}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load audit trend: ${response.status}`));
    }
    return response.json();
  },

  async getAuditFeedback() {
    const response = await apiFetch(`${ADMIN_API_BASE_URL}/api/audit/feedback`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load user feedback: ${response.status}`));
    }
    return response.json();
  },
};
